import React from 'react';
import { SEO } from '../components/seo/SEO';
import { Home, Heart, CheckCircle2, AlertTriangle, HelpCircle, Phone } from 'lucide-react';
import { getBreadcrumbSchema } from '../lib/jsonLd';

export function FosterHomePage() {
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Strona główna', url: 'https://znajdki.pl/' },
    { name: 'Dom tymczasowy', url: 'https://znajdki.pl/dom-tymczasowy' }
  ]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <SEO 
        title="Dom tymczasowy dla zwierząt - Fundacja Znajdki"
        description="Zostań domem tymczasowym dla bezdomnych psów i kotów. Pomóż zwierzętom przygotować się do adopcji w domu tymczasowym Fundacji Znajdki."
        url="/dom-tymczasowy"
        image="/images/animals/fundacja-znajdki-img-3229.webp"
        jsonLd={breadcrumbSchema}
      />

      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="text-accessible-gray-900 dark:text-white high-contrast:text-white mb-6">
          Zostań domem tymczasowym
        </h1>
        <div className="relative w-full max-w-4xl mx-auto mb-12 rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="/images/animals/fundacja-znajdki-img-3229.webp"
            alt="Pies i kot w domu – Fundacja Znajdki"
            className="w-full h-64 sm:h-72 md:h-80 lg:h-[500px] xl:h-[600px] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <p className="heading-4-white font-medium max-w-2xl mx-auto no-margin">
              Podaruj zwierzakowi bezpieczną przystań w drodze do stałego domu
            </p>
          </div>
        </div>
        <p className="text-xl text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white max-w-3xl mx-auto">
          Dom tymczasowy to miejsce, gdzie bezdomne zwierzęta mogą dojść do siebie,
          nabrać zaufania do ludzi i przygotować się do adopcji.
        </p>
      </div>

      {/* W prostym języku */}
      <section className="mb-8">
        <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-6 rounded-lg shadow-lg">
          <h2 className="heading-4 no-margin">W skrócie</h2>
          <p className="body-medium no-margin mt-2">Dom tymczasowy to opieka nad zwierzakiem do czasu adopcji. Zapewniasz bezpieczne miejsce i podstawową opiekę, a my wspieramy leczenie i karmę.</p>
        </div>
      </section>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Column */}
        <div className="space-y-8">
          {/* What is a Foster Home */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <Home className="w-6 h-6 text-accessible-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white">
                Czym jest dom tymczasowy?
              </h2>
            </div>
            
            <div className="space-y-4 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
              <p>
                Dom tymczasowy to miejsce, gdzie zwierzę przebywa do czasu znalezienia stałego domu.
                Jako opiekun tymczasowy zapewniasz:
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span>Bezpieczne schronienie i opiekę</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span>Socjalizację i naukę życia z ludźmi</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span>Pomoc w znalezieniu stałego domu</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                  <span>Miłość i cierpliwość w procesie adaptacji</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Requirements */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-accessible-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white">
                Wymagania
              </h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-3">
                  Dla kotów:
                </h3>
                <ul className="space-y-2 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                    <span>Zabezpieczone okna i balkony</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                    <span>Osobne pomieszczenie na kwarantannę</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                    <span>Brak możliwości wychodzenia na zewnątrz</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-3">
                  Dla psów:
                </h3>
                <ul className="space-y-2 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                    <span>Możliwość regularnych spacerów</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                    <span>Bezpieczne ogrodzenie (jeśli jest ogród)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0" />
                    <span>Zgoda wszystkich domowników</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* What We Provide */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-accessible-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white">
                Co zapewniamy?
              </h2>
            </div>
            
            <div className="space-y-4 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block text-accessible-gray-900 dark:text-white high-contrast:text-white">Opiekę weterynaryjną</strong>
                    <span>Pokrywamy wszystkie koszty leczenia i profilaktyki</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block text-accessible-gray-900 dark:text-white high-contrast:text-white">Karmę i akcesoria</strong>
                    <span>Zapewniamy podstawowe wyposażenie i jedzenie</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block text-accessible-gray-900 dark:text-white high-contrast:text-white">Wsparcie behawioralne</strong>
                    <span>Pomagamy w rozwiązywaniu problemów z zachowaniem</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accessible-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block text-accessible-gray-900 dark:text-white high-contrast:text-white">Transport</strong>
                    <span>Pomagamy w dowozie zwierzaka i wizytach u weterynarza</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-8 rounded-lg shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-accessible-red-600/10 flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-accessible-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-accessible-gray-900 dark:text-white high-contrast:text-white">
                Często zadawane pytania
              </h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-2">
                  Jak długo trwa dom tymczasowy?
                </h3>
                <p className="text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
                  To zależy od zwierzaka - może to być kilka tygodni lub miesięcy. Zawsze ustalamy to indywidualnie.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-2">
                  Czy mogę adoptować swojego podopiecznego?
                </h3>
                <p className="text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
                  Tak, jeśli podczas opieki tymczasowej przywiążesz się do zwierzaka, możesz go adoptować na stałe.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-2">
                  Co jeśli mam już zwierzęta?
                </h3>
                <p className="text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
                  To nie problem - dobierzemy zwierzaka, który będzie pasował do Twojej rodziny.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="mt-16 bg-accessible-red-600 rounded-lg p-8 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-4">
            <Phone className="w-12 h-12" />
            <div>
              <h2 className="heading-2-white mb-2">
                Chcesz zostać domem tymczasowym?
              </h2>
              <p className="text-white/90">
                Zadzwoń do nas i porozmawiajmy o szczegółach
              </p>
            </div>
          </div>
          <a 
            href="tel:+48664821160"
            className="bg-white text-accessible-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-accessible-gray-50 transition-colors flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            <span>664 821 160</span>
          </a>
        </div>
      </div>
    </div>
  );
}